// ═══════════════════════════════════════════════════════════════
// PA Grants — RFI List Page
// All RFIs across projects, filterable by status / project / priority
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Text, Group, Stack, Badge, Button, Select, TextInput, Modal, Textarea,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconSearch, IconMessageQuestion, IconArrowRight, IconClock,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import type { PAProjectRfi, RfiStatus, RfiPriority, RfiCategory } from '../../types/pagrants';

// ── Helpers ─────────────────────────────────────────────────────

function fmtDate(v: string | undefined | null) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_COLORS: Record<RfiStatus, string> = {
  open: '#D97706',
  responded: '#2563EB',
  in_review: '#7C3AED',
  resolved: '#059669',
  closed: '#6B7280',
};

const STATUS_LABELS: Record<RfiStatus, string> = {
  open: 'Open',
  responded: 'Responded',
  in_review: 'In Review',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_COLORS: Record<RfiPriority, string> = {
  low: '#6B7280',
  normal: '#2563EB',
  high: '#D97706',
  urgent: '#DC2626',
};

const CATEGORY_LABELS: Record<RfiCategory, string> = {
  documentation: 'Documentation',
  clarification: 'Clarification',
  eligibility: 'Eligibility',
  site_access: 'Site Access',
  cost_justification: 'Cost Justification',
  environmental: 'Environmental',
  procurement: 'Procurement',
  other: 'Other',
};

export default function RFIs() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);

  const rfis = usePAGrantsStore(s => s.rfis);
  const rfisLoading = usePAGrantsStore(s => s.rfisLoading);
  const fetchRfis = usePAGrantsStore(s => s.fetchRfis);
  const createRfi = usePAGrantsStore(s => s.createRfi);
  const projects = usePAGrantsStore(s => s.projects);
  const fetchProjects = usePAGrantsStore(s => s.fetchProjects);
  const organizations = usePAGrantsStore(s => s.organizations);
  const fetchOrganizations = usePAGrantsStore(s => s.fetchOrganizations);
  const activeDisasterId = usePAGrantsStore(s => s.activeDisasterId);
  const disasters = usePAGrantsStore(s => s.disasters);

  // ── Filters ──────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterProject, setFilterProject] = useState<string>('');

  // ── Sort ─────────────────────────────────────────────────────
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };

  // ── Create modal ─────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [newProjectId, setNewProjectId] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newCategory, setNewCategory] = useState<string>('documentation');
  const [newPriority, setNewPriority] = useState<string>('normal');
  const [newDueDate, setNewDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRfis();
    fetchProjects();
    fetchOrganizations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived data ─────────────────────────────────────────────
  const orgMap = useMemo(() =>
    Object.fromEntries(organizations.map(o => [String(o.Rid), o.organizationName])),
    [organizations]);

  const projectMap = useMemo(() =>
    Object.fromEntries(projects.map(p => [String(p.Rid), p])),
    [projects]);

  const disasterFilter = activeDisasterId
    ? rfis.filter(r => !r.disasterId || r.disasterId === activeDisasterId)
    : rfis;

  const filtered = useMemo(() => {
    let list = disasterFilter;
    if (filterStatus) list = list.filter(r => r.status === filterStatus);
    if (filterPriority) list = list.filter(r => r.priority === filterPriority);
    if (filterProject) list = list.filter(r => r.projectId === filterProject);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.rfiNumber.toLowerCase().includes(q) ||
        r.subject.toLowerCase().includes(q) ||
        (projectMap[r.projectId]?.projectName || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const va = (a as Record<string, unknown>)[sortField] ?? '';
      const vb = (b as Record<string, unknown>)[sortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [disasterFilter, filterStatus, filterPriority, filterProject, search, sortField, sortDir, projectMap]);

  // Stats
  const stats = useMemo(() => {
    const open = rfis.filter(r => r.status === 'open').length;
    const responded = rfis.filter(r => r.status === 'responded').length;
    const inReview = rfis.filter(r => r.status === 'in_review').length;
    const urgent = rfis.filter(r => r.priority === 'urgent' && !['resolved','closed'].includes(r.status)).length;
    return { open, responded, inReview, urgent };
  }, [rfis]);

  // Project options (for filter + create modal)
  const projectOptions = useMemo(() => [
    { value: '', label: 'All Projects' },
    ...projects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber || p.Rid} — ${p.projectName || '(unnamed)'}` })),
  ], [projects]);

  const createProjectOptions = useMemo(() =>
    projects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber || p.Rid} — ${p.projectName || '(unnamed)'}` })),
    [projects]);

  async function handleCreate() {
    if (!newSubject.trim() || !newProjectId) return;
    setSaving(true);
    const proj = projectMap[newProjectId];
    const id = await createRfi({
      projectId: newProjectId,
      organizationId: proj?.organizationId || '',
      disasterId: proj?.disasterId || activeDisasterId || '',
      subject: newSubject.trim(),
      body: newBody.trim(),
      category: newCategory as RfiCategory,
      priority: newPriority as RfiPriority,
      dueDate: newDueDate || undefined,
      createdBy: user?.email || '',
    });
    setSaving(false);
    if (id) {
      setShowCreate(false);
      setNewSubject(''); setNewBody(''); setNewProjectId('');
      setNewCategory('documentation'); setNewPriority('normal'); setNewDueDate('');
    }
  }

  const thStyle = (col: string): React.CSSProperties => ({
    padding: '8px 12px', fontSize: 12, fontWeight: 600, color: sortField === col ? '#1E40AF' : '#64748b',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em',
  });

  const sortIndicator = (col: string) =>
    sortField === col
      ? (sortDir === 'desc' ? ' ↓' : ' ↑')
      : <span style={{ opacity: 0.25 }}> ↑</span>;

  return (
    <div>
      {/* ── Page header ── */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconMessageQuestion size={22} color="#1E40AF" />
          <Text fw={700} size="xl">Requests for Information</Text>
        </Group>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          style={{ background: '#1E40AF', color: '#fff', border: 'none' }}
          onClick={() => setShowCreate(true)}
        >
          New RFI
        </Button>
      </Group>

      {/* ── Stats row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Open', value: stats.open, color: '#D97706' },
          { label: 'Responded', value: stats.responded, color: '#2563EB' },
          { label: 'In Review', value: stats.inReview, color: '#7C3AED' },
          { label: 'Urgent', value: stats.urgent, color: '#DC2626' },
        ].map(s => (
          <Paper key={s.label} p="sm" withBorder style={{ textAlign: 'center', borderColor: '#e2e8f0' }}>
            <Text style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Text>
            <Text size="xs" c="dimmed" mt={4}>{s.label}</Text>
          </Paper>
        ))}
      </div>

      {/* ── Filters ── */}
      <Paper p="sm" withBorder mb="md" style={{ borderColor: '#e2e8f0' }}>
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search RFI #, subject, project…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            style={{ flex: '1 1 220px', minWidth: 180 }}
            size="sm"
          />
          <Select
            size="sm"
            placeholder="Status"
            data={[
              { value: '', label: 'All Statuses' },
              { value: 'open', label: 'Open' },
              { value: 'responded', label: 'Responded' },
              { value: 'in_review', label: 'In Review' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'closed', label: 'Closed' },
            ]}
            value={filterStatus}
            onChange={v => setFilterStatus(v || '')}
            style={{ width: 150 }}
          />
          <Select
            size="sm"
            placeholder="Priority"
            data={[
              { value: '', label: 'All Priorities' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'high', label: 'High' },
              { value: 'normal', label: 'Normal' },
              { value: 'low', label: 'Low' },
            ]}
            value={filterPriority}
            onChange={v => setFilterPriority(v || '')}
            style={{ width: 140 }}
          />
          <Select
            size="sm"
            data={projectOptions}
            value={filterProject}
            onChange={v => setFilterProject(v || '')}
            style={{ flex: '1 1 240px', minWidth: 200 }}
          />
        </Group>
      </Paper>

      {/* ── Table ── */}
      <Paper withBorder style={{ borderColor: '#e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
          {rfisLoading ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <Text c="dimmed" size="sm">Loading…</Text>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <IconMessageQuestion size={40} color="#cbd5e1" />
              <Text c="dimmed" size="sm" mt="xs">No RFIs found</Text>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={thStyle('rfiNumber')} onClick={() => toggleSort('rfiNumber')}>RFI #{sortIndicator('rfiNumber')}</th>
                  <th style={thStyle('subject')} onClick={() => toggleSort('subject')}>Subject{sortIndicator('subject')}</th>
                  <th style={thStyle('projectId')} onClick={() => toggleSort('projectId')}>Project{sortIndicator('projectId')}</th>
                  <th style={thStyle('category')} onClick={() => toggleSort('category')}>Category{sortIndicator('category')}</th>
                  <th style={thStyle('priority')} onClick={() => toggleSort('priority')}>Priority{sortIndicator('priority')}</th>
                  <th style={thStyle('status')} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                  <th style={thStyle('dueDate')} onClick={() => toggleSort('dueDate')}>Due{sortIndicator('dueDate')}</th>
                  <th style={thStyle('created_at')} onClick={() => toggleSort('created_at')}>Created{sortIndicator('created_at')}</th>
                  <th style={{ padding: '8px 12px', width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map(rfi => {
                  const proj = projectMap[rfi.projectId];
                  const isDue = rfi.dueDate && new Date(rfi.dueDate) < new Date() && !['resolved','closed'].includes(rfi.status);
                  return (
                    <tr
                      key={rfi.id}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                      onClick={() => {
                        if (proj) {
                          navigate(`/pagrants/pa/projects/${proj.Rid}?tab=rfis`);
                        }
                      }}
                    >
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1E40AF', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {rfi.rfiNumber || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 13, maxWidth: 280 }}>
                        <Text size="sm" fw={500} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                          {rfi.subject}
                        </Text>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 13, whiteSpace: 'nowrap' }}>
                        <Text size="xs" c="dimmed">{proj?.projectNumber || '—'}</Text>
                        <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                          {proj?.projectName || '—'}
                        </Text>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, whiteSpace: 'nowrap' }}>
                        <Badge size="sm" variant="light" style={{ background: '#f1f5f9', color: '#475569' }}>
                          {CATEGORY_LABELS[rfi.category as RfiCategory] || rfi.category}
                        </Badge>
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <Badge size="sm" variant="dot" style={{ color: PRIORITY_COLORS[rfi.priority as RfiPriority] || '#64748b' }}>
                          {rfi.priority}
                        </Badge>
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <Badge
                          size="sm"
                          style={{
                            background: STATUS_COLORS[rfi.status as RfiStatus] + '22',
                            color: STATUS_COLORS[rfi.status as RfiStatus],
                            border: `1px solid ${STATUS_COLORS[rfi.status as RfiStatus]}44`,
                          }}
                        >
                          {STATUS_LABELS[rfi.status as RfiStatus] || rfi.status}
                        </Badge>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {rfi.dueDate ? (
                          <Group gap={4}>
                            {isDue && <IconClock size={12} color="#DC2626" />}
                            <Text size="xs" style={{ color: isDue ? '#DC2626' : '#64748b' }}>
                              {fmtDate(rfi.dueDate)}
                            </Text>
                          </Group>
                        ) : <Text size="xs" c="dimmed">—</Text>}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                        {fmtDate(rfi.created_at)}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <IconArrowRight size={14} color="#94a3b8" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: '8px 16px', borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
          <Text size="xs" c="dimmed">{filtered.length} of {rfis.length} RFIs</Text>
        </div>
      </Paper>

      {/* ── Create RFI modal ── */}
      <Modal
        opened={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Request for Information"
        size="lg"
      >
        <Stack gap="sm">
          <Select
            label="Project"
            placeholder="Select project…"
            data={createProjectOptions}
            value={newProjectId}
            onChange={v => setNewProjectId(v || '')}
            required
            searchable
          />
          <TextInput
            label="Subject"
            placeholder="Brief description of the information needed"
            value={newSubject}
            onChange={e => setNewSubject(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Body"
            placeholder="Detailed description, context, specific documents or data requested…"
            value={newBody}
            onChange={e => setNewBody(e.currentTarget.value)}
            minRows={4}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select
              label="Category"
              data={Object.entries(CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              value={newCategory}
              onChange={v => setNewCategory(v || 'other')}
            />
            <Select
              label="Priority"
              data={[
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
              value={newPriority}
              onChange={v => setNewPriority(v || 'normal')}
            />
          </div>
          <TextInput
            label="Due Date"
            type="date"
            value={newDueDate}
            onChange={e => setNewDueDate(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              size="sm"
              style={{ background: '#1E40AF', color: '#fff', border: 'none' }}
              loading={saving}
              disabled={!newSubject.trim() || !newProjectId}
              onClick={handleCreate}
            >
              Create RFI
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
